/**
 * App Component
 * Main Ink application for the CLI chat interface
 * Uses Paean brand styling throughout
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Text, useApp, useInput } from 'ink';
import { Header, InputArea, MarkdownText, Spinner, StatusBar, StreamingText, ToolCallHistory } from './components/index.js';
import { useAgentStream, useCommands, useGateway, useWechat } from './hooks/index.js';
import { INK_COLORS, SYMBOLS } from './theme/ink-theme.js';
import type { McpState, McpToolResult } from '../agent/types.js';
import type { WechatGatewayService } from '../wechat/service.js';

/** Time window for double Ctrl+C to force exit (ms) */
const DOUBLE_CTRL_C_WINDOW = 1500;

function renderUsageBar(percent: number, width = 20): string {
    const p = Math.max(0, Math.min(100, percent));
    const filled = Math.round((p / 100) * width);
    return '█'.repeat(filled) + '░'.repeat(width - filled);
}

function formatUsageOutput(
    status: {
        credits: number;
        totalCredits: number;
        subscriptionTier: string;
        nextRecoveryAt: string | null;
        canRecover: boolean;
    },
    limits: {
        usage5h: number;
        usage7d: number;
        limits: { credits5h: number; credits7d: number };
        percentUsed5h: number;
        percentUsed7d: number;
    },
): string {
    const bar5h = renderUsageBar(limits.percentUsed5h);
    const bar7d = renderUsageBar(limits.percentUsed7d);

    let out = '\n  ─── Usage ──────────────────────────────────\n\n';
    out += `    Subscription:    ${status.subscriptionTier}\n`;
    out += `    Credits:         ${status.credits} / ${status.totalCredits}\n`;
    if (status.canRecover) {
        out += `    Recovery:        available now\n`;
    } else if (status.nextRecoveryAt) {
        const d = new Date(status.nextRecoveryAt);
        out += `    Next recovery:   ${d.toLocaleString()}\n`;
    }
    out += '\n  ─── Rolling Windows ────────────────────────\n\n';
    out += `    5h   ${bar5h}  ${limits.percentUsed5h}%  (${limits.usage5h} / ${limits.limits.credits5h})\n`;
    out += `    7d   ${bar7d}  ${limits.percentUsed7d}%  (${limits.usage7d} / ${limits.limits.credits7d})\n`;

    if (limits.percentUsed5h >= 90 || limits.percentUsed7d >= 90) {
        out += '\n  ⚠  Approaching usage limits — requests may be throttled.\n';
    }
    return out;
}

export interface AppProps {
    mcpState?: McpState;
    onMcpToolCall?: (
        callId: string,
        serverName: string,
        toolName: string,
        args: Record<string, unknown>
    ) => Promise<McpToolResult>;
    mcpClient?: unknown;
    debug?: boolean;
    initialMessage?: string;
    resumeConversationId?: string;
    gatewayEnabled?: boolean;
    gatewayPollInterval?: number;
    gatewaySessionName?: string;
    wechatService?: WechatGatewayService;
    modelTier?: 'lite' | 'flash' | 'pro';
}

export const App: React.FC<AppProps> = ({
    mcpState, onMcpToolCall, mcpClient, debug: _debug = false,
    initialMessage, resumeConversationId,
    gatewayEnabled = false, gatewayPollInterval, gatewaySessionName,
    wechatService: wechatServiceProp,
    modelTier,
}) => {
    const { exit } = useApp();
    const [commandOutput, setCommandOutput] = useState<string | null>(null);
    const [commandSuggestions, setCommandSuggestions] = useState<string[]>([]);

    // Calculate MCP tool count with memoization
    const mcpToolCount = useMemo(() => {
        return mcpState?.mcpServers?.reduce(
            (sum, server) => sum + (server.tools?.length || 0),
            0
        ) || 0;
    }, [mcpState?.mcpServers]);

    // Gateway — can be toggled at runtime via /gateway
    const [gatewayActive, setGatewayActive] = useState(gatewayEnabled);

    const gateway = useGateway({
        enabled: gatewayActive,
        pollInterval: gatewayPollInterval,
        sessionName: gatewaySessionName,
        debug: _debug,
        mcpState,
        onMcpToolCall,
        mcpClient,
    });

    const wechat = useWechat({
        service: wechatServiceProp ?? null,
    });

    // Service status ref: read lazily by /status commands
    const serviceStatusRef = useRef<{
        gatewayOnline: boolean;
        gatewayCompleted: number;
        heartbeatActive: boolean;
        heartbeatLastPoll?: string;
        heartbeatTriggered: number;
    }>({
        gatewayOnline: false, gatewayCompleted: 0,
        heartbeatActive: false, heartbeatTriggered: 0,
    });

    // Initialize hooks
    const { rawMode, compactMode, heartbeatMode, sessionStats, handleCommand, getCompletions, trackToolCall: _trackToolCall } = useCommands({
        mcpServers: mcpState?.mcpServers,
        serviceStatusRef,
    });

    const {
        messages,
        isProcessing,
        toolCallHistory,
        streamingText,
        conversationId,
        sendMessage,
        abort,
        addRemoteExchange,
        queuedMessage,
        enqueueMessage,
        clearQueuedMessage,
    } = useAgentStream({
        mcpState,
        onMcpToolCall,
        cliMode: rawMode,
        resumeConversationId,
        modelTier,
    });

    // Cloud scheduled task polling — checks for due tasks and triggers them
    const heartbeatTriggeredRef = useRef(0);
    const heartbeatLastPollRef = useRef<string | undefined>(undefined);
    const sessionIdRef = useRef<string | null>(null);

    // Generate a stable session ID for distributed claim locking
    useEffect(() => {
        (async () => {
            try {
                const { getConfig } = await import('../utils/config.js');
                const config = getConfig();
                if (config.deviceSessionId) {
                    sessionIdRef.current = config.deviceSessionId;
                } else if (config.token) {
                    const crypto = await import('crypto');
                    const os = await import('os');
                    const hash = crypto.createHash('sha256')
                        .update(`${os.hostname()}-${config.token.slice(0, 32)}`)
                        .digest('hex')
                        .slice(0, 32);
                    sessionIdRef.current = `cli-poll-${hash}`;
                }
            } catch { /* best effort */ }
        })();
    }, []);

    useEffect(() => {
        if (!heartbeatMode) return;

        const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

        const pollScheduledTasks = async () => {
            try {
                const { getScheduledTasks, claimScheduleTrigger, triggerSchedule } = await import('../api/todo.js');
                const result = await getScheduledTasks({ enabled: true });
                heartbeatLastPollRef.current = new Date().toLocaleTimeString();

                if (!result.success || !result.data?.items?.length) return;

                const now = new Date();
                const sid = sessionIdRef.current;

                for (const item of result.data.items) {
                    const nextTrigger = item.schedule?.nextTriggerAt;
                    if (nextTrigger && new Date(nextTrigger) <= now) {
                        try {
                            if (sid) {
                                // Atomic claim to prevent duplicate execution across CLI instances
                                const claim = await claimScheduleTrigger(item.id, sid);
                                if (claim.success && claim.claimed) {
                                    heartbeatTriggeredRef.current++;
                                }
                            } else {
                                // Fallback: trigger directly (no distributed lock)
                                await triggerSchedule(item.id);
                                heartbeatTriggeredRef.current++;
                            }
                        } catch {
                            // Best effort — will retry next poll
                        }
                    }
                }
            } catch {
                // Network error — will retry next poll
            }
        };

        // Poll immediately on enable, then every 5 minutes
        pollScheduledTasks();
        const timer = setInterval(pollScheduledTasks, HEARTBEAT_INTERVAL);
        return () => clearInterval(timer);
    }, [heartbeatMode]);

    // Keep the shared ref up to date so useCommands can read latest values lazily
    serviceStatusRef.current = {
        gatewayOnline: gateway.isOnline,
        gatewayCompleted: gateway.completedCount,
        heartbeatActive: heartbeatMode,
        heartbeatLastPoll: heartbeatLastPollRef.current,
        heartbeatTriggered: heartbeatTriggeredRef.current,
    };

    // Persist completed remote exchanges into message history
    useEffect(() => {
        if (gateway.lastCompletedExchange) {
            addRemoteExchange(
                gateway.lastCompletedExchange.userMessage,
                gateway.lastCompletedExchange.assistantContent,
                gateway.lastCompletedExchange.clientType,
            );
            gateway.clearCompletedExchange();
        }
    }, [gateway.lastCompletedExchange, addRemoteExchange, gateway.clearCompletedExchange]);

    // Persist completed WeChat exchanges into message history
    useEffect(() => {
        if (wechat.lastCompletedExchange) {
            addRemoteExchange(
                wechat.lastCompletedExchange.userMessage,
                wechat.lastCompletedExchange.assistantContent,
                'wechat',
            );
            wechat.clearCompletedExchange();
        }
    }, [wechat.lastCompletedExchange, addRemoteExchange, wechat.clearCompletedExchange]);

    // Print resume command on exit
    const printResumeCommand = useCallback(() => {
        if (conversationId) {
            console.log(`\nTo resume this session: paean --resume=${conversationId}\n`);
        }
    }, [conversationId]);

    // Track if initial message was sent
    const initialMessageSentRef = useRef(false);
    const isSingleMessageMode = !!initialMessage;

    // Send initial message on mount if provided
    useEffect(() => {
        if (initialMessage && !initialMessageSentRef.current && !isProcessing) {
            initialMessageSentRef.current = true;
            sendMessage(initialMessage);
        }
    }, [initialMessage, sendMessage, isProcessing]);

    // Auto-exit after single message completes
    useEffect(() => {
        if (isSingleMessageMode && !isProcessing && messages.length > 0) {
            // Check if we have both user and assistant messages
            const hasUserMessage = messages.some(m => m.role === 'user');
            const hasAssistantMessage = messages.some(m => m.role === 'assistant');
            if (hasUserMessage && hasAssistantMessage) {
                // Small delay to ensure message is displayed
                const timer = setTimeout(() => {
                    printResumeCommand();
                    exit();
                }, 100);
                return () => clearTimeout(timer);
            }
        }
        return undefined;
    }, [isSingleMessageMode, isProcessing, messages, exit, printResumeCommand]);

    // Handle Ctrl+C / Escape: abort active work, force exit on double Ctrl+C
    const lastCtrlCRef = useRef(0);

    useInput((input, key) => {
        // Escape key: cancel ongoing request (Gemini CLI best practice)
        if (key.escape) {
            const anyBusy = isProcessing || gateway.isProcessingRemote || wechat.isProcessingRemote;
            if (anyBusy) {
                if (isProcessing) {
                    abort();
                    // Also clear any queued message on escape
                    clearQueuedMessage();
                }
            }
            return;
        }

        if (key.ctrl && input === 'c') {
            const now = Date.now();
            const anyBusy = isProcessing || gateway.isProcessingRemote || wechat.isProcessingRemote;

            if (anyBusy) {
                if (isProcessing) {
                    abort();
                    clearQueuedMessage();
                }

                if (now - lastCtrlCRef.current < DOUBLE_CTRL_C_WINDOW) {
                    printResumeCommand();
                    process.exit(130);
                }
                lastCtrlCRef.current = now;
            } else {
                printResumeCommand();
                exit();
                setTimeout(() => process.exit(130), 500);
            }
        }
    });

    // Handle input changes for command suggestions
    const handleInputChange = useCallback((value: string) => {
        if (value.startsWith('/') && value.length > 0) {
            const completions = getCompletions(value);
            setCommandSuggestions(completions);
        } else {
            setCommandSuggestions([]);
        }
    }, [getCompletions]);

    // Handle Tab completion
    const handleTabComplete = useCallback((value: string): string | null => {
        const completions = getCompletions(value);
        if (completions.length === 1) {
            setCommandSuggestions([]);
            return completions[0];
        }
        return null;
    }, [getCompletions]);

    // Handle user input submission
    const handleSubmit = useCallback((input: string) => {
        // Clear previous command output and suggestions
        setCommandOutput(null);
        setCommandSuggestions([]);

        // Check if it's a command
        if (input.startsWith('/')) {
            // Async slash commands (must run before sync handleCommand so they don't hit the default "unknown" case)
            if (input.trim().toLowerCase() === '/usage') {
                setCommandOutput('  Fetching usage…');
                (async () => {
                    try {
                        const { getCreditsStatus, getUsageLimits } = await import('../api/credits.js');
                        const [statusRes, limitsRes] = await Promise.all([
                            getCreditsStatus(),
                            getUsageLimits(),
                        ]);
                        setCommandOutput(formatUsageOutput(statusRes.data, limitsRes.data));
                    } catch (err: any) {
                        setCommandOutput(`\n  Failed to fetch usage: ${err?.message || err}\n`);
                    }
                })();
                return;
            }

            const result = handleCommand(input);
            if (result.handled) {
                if (result.action === 'exit') {
                    printResumeCommand();
                    import('../mcp/loop.js').then(({ clearAllLoopJobs }) => clearAllLoopJobs()).catch(() => {});
                    exit();
                    setTimeout(() => process.exit(0), 500);
                    return;
                }
                if (result.action === 'toggle-gateway') {
                    setGatewayActive(prev => !prev);
                }
                if (result.output) {
                    setCommandOutput(result.output);
                }
                return;
            }

            if (input.toLowerCase().startsWith('/loop ')) {
                const loopDesc = input.slice(6).trim();
                if (loopDesc) {
                    const loopMsg = `[User requested /loop] Set up a session-scoped loop task: ${loopDesc}. Use the paean_loop_create tool to create it. Parse the natural language to determine the schedule and prompt.`;
                    if (isProcessing) {
                        enqueueMessage(loopMsg);
                    } else {
                        sendMessage(loopMsg);
                    }
                    return;
                }
            }
        }

        // If currently processing, queue the message for auto-send when done
        if (isProcessing) {
            enqueueMessage(input);
        } else {
            sendMessage(input);
        }
    }, [handleCommand, sendMessage, exit, printResumeCommand, isProcessing, enqueueMessage]);

    // Only disable input for remote gateway/wechat processing.
    // During local processing, the user can type and queue their next message.
    const inputDisabled = gateway.isProcessingRemote || wechat.isProcessingRemote;

    return (
        <Box flexDirection="column" paddingX={1}>
            {/* Header */}
            <Header
                mcpToolCount={mcpToolCount}
                gatewayOnline={gateway.isOnline}
                gatewayCompleted={gateway.completedCount}
                wechatOnline={wechat.isOnline}
                wechatCompleted={wechat.completedCount}
            />

            {/* Message History */}
            {useMemo(() => messages.map((msg) => (
                <Box key={msg.id} flexDirection="column" marginBottom={1}>
                    {msg.role === 'user' ? (
                        <Box>
                            <Text color={INK_COLORS.primary} bold>
                                {SYMBOLS.prompt}{' '}
                                {(msg as any).source === 'remote'
                                    ? `Remote (${(msg as any).clientType || 'web'})`
                                    : 'You'
                                }
                            </Text>
                        </Box>
                    ) : (
                        <Box flexDirection="column">
                            <Box marginBottom={0}>
                                <Text color={INK_COLORS.secondary} bold>{SYMBOLS.arch} Pæan</Text>
                                <Text color="gray" dimColor> {SYMBOLS.bulletSm} assistant</Text>
                            </Box>
                            <Box paddingLeft={2}>
                                <MarkdownText>{msg.content}</MarkdownText>
                            </Box>
                        </Box>
                    )}
                    {msg.role === 'user' && (
                        <Box paddingLeft={2}>
                            <Text>{msg.content}</Text>
                        </Box>
                    )}
                </Box>
            )), [messages])}

            {/* Command Output */}
            {commandOutput && (
                <Box marginBottom={1}>
                    <Text dimColor>{commandOutput}</Text>
                </Box>
            )}

            {/* Local Tool Call History — bordered group with inline results */}
            {isProcessing && toolCallHistory.length > 0 && (
                <Box marginBottom={1}>
                    <ToolCallHistory history={toolCallHistory} compactMode={compactMode} />
                </Box>
            )}

            {/* Local Streaming Response */}
            {isProcessing && streamingText && (
                <Box marginBottom={1}>
                    <StreamingText
                        text={streamingText}
                        isComplete={false}
                        rawMode={rawMode}
                    />
                </Box>
            )}

            {/* Local Loading Spinner */}
            {isProcessing && !streamingText && toolCallHistory.length === 0 && (
                <Box marginBottom={1}>
                    <Spinner label="Thinking..." type="thinking" />
                </Box>
            )}

            {/* Remote Request Banner */}
            {gateway.isProcessingRemote && gateway.remoteRequest && (
                <Box flexDirection="column" marginBottom={1}>
                    <Box>
                        <Text color="#10B981">⬡ </Text>
                        <Text color="#10B981" bold>Remote </Text>
                        <Text dimColor>({gateway.remoteRequest.clientType}): </Text>
                        <Text>{gateway.remoteRequest.message.length > 80
                            ? gateway.remoteRequest.message.slice(0, 80) + '...'
                            : gateway.remoteRequest.message
                        }</Text>
                    </Box>
                </Box>
            )}

            {/* Remote Tool Call History */}
            {gateway.isProcessingRemote && gateway.remoteToolCallHistory.length > 0 && (
                <Box marginBottom={1}>
                    <ToolCallHistory history={gateway.remoteToolCallHistory} compactMode={compactMode} />
                </Box>
            )}

            {/* Remote Streaming Response */}
            {gateway.isProcessingRemote && gateway.remoteStreamingText && (
                <Box marginBottom={1}>
                    <StreamingText
                        text={gateway.remoteStreamingText}
                        isComplete={false}
                        rawMode={rawMode}
                    />
                </Box>
            )}

            {/* Remote Loading Spinner */}
            {gateway.isProcessingRemote && !gateway.remoteStreamingText && gateway.remoteToolCallHistory.length === 0 && (
                <Box marginBottom={1}>
                    <Spinner label="Processing remote request..." type="thinking" />
                </Box>
            )}

            {/* Gateway Status Message */}
            {gateway.statusMessage && (
                <Box marginBottom={1}>
                    <Text color="yellow">  {gateway.statusMessage}</Text>
                </Box>
            )}

            {/* WeChat Request Banner */}
            {wechat.isProcessingRemote && wechat.remoteRequest && (
                <Box flexDirection="column" marginBottom={1}>
                    <Box>
                        <Text color="#07C160">💬 </Text>
                        <Text color="#07C160" bold>WeChat </Text>
                        <Text dimColor>({wechat.remoteRequest.sender.split('@')[0] || wechat.remoteRequest.sender}): </Text>
                        <Text>{wechat.remoteRequest.text.length > 80
                            ? wechat.remoteRequest.text.slice(0, 80) + '...'
                            : wechat.remoteRequest.text
                        }</Text>
                    </Box>
                </Box>
            )}

            {/* WeChat Tool Call History */}
            {wechat.isProcessingRemote && wechat.remoteToolCallHistory.length > 0 && (
                <Box marginBottom={1}>
                    <ToolCallHistory history={wechat.remoteToolCallHistory} compactMode={compactMode} />
                </Box>
            )}

            {/* WeChat Streaming Response */}
            {wechat.isProcessingRemote && wechat.remoteStreamingText && (
                <Box marginBottom={1}>
                    <StreamingText
                        text={wechat.remoteStreamingText}
                        isComplete={false}
                        rawMode={rawMode}
                    />
                </Box>
            )}

            {/* WeChat Loading Spinner */}
            {wechat.isProcessingRemote && !wechat.remoteStreamingText && wechat.remoteToolCallHistory.length === 0 && (
                <Box marginBottom={1}>
                    <Spinner label="Processing WeChat message..." type="thinking" />
                </Box>
            )}

            {/* WeChat Error */}
            {wechat.remoteError && (
                <Box marginBottom={1}>
                    <Text color="yellow">  WeChat: {wechat.remoteError}</Text>
                </Box>
            )}

            {/* Status Bar — shows session context */}
            {!isSingleMessageMode && (sessionStats.toolCallCount > 0 || conversationId) && (
                <StatusBar
                    conversationId={conversationId}
                    stats={sessionStats}
                />
            )}

            {/* Queued message indicator */}
            {queuedMessage && (
                <Box marginBottom={0}>
                    <Text dimColor>
                        {'  '}{SYMBOLS.queue} Queued: {queuedMessage.length > 60 ? queuedMessage.slice(0, 60) + SYMBOLS.ellipsis : queuedMessage}
                    </Text>
                </Box>
            )}

            {/* Input Area - Hide for single message mode, disabled during remote processing */}
            {!isSingleMessageMode && (
                <InputArea
                    onSubmit={handleSubmit}
                    onInputChange={handleInputChange}
                    onTabComplete={handleTabComplete}
                    suggestions={commandSuggestions}
                    disabled={inputDisabled}
                    placeholder={isProcessing ? 'Type to queue next message… (Esc to cancel)' : undefined}
                />
            )}
        </Box>
    );
};

